import React from "react";
import PropTypes from "prop-types";
import EntryPicker from "../common/EntryPicker";
import UnderlineInput from "../common/UnderlineInput";
import HsbcXmlV3Fields, { effectivePaymentType, missingXmlV3 } from "./HsbcXmlV3Fields";

const FIELDS = [
  ["recipient_name", "Recipient name", "payee"],
  ["bank_account", "Recipient bank account", "bank_account"],
  ["bank_name", "Recipient bank name", "bank_name"],
  ["bank_bic", "Bank BIC / SWIFT", "bank_bic"],
];

// Where a payment value comes from — shown under each field so Accounting knows what to
// fix where. `bic_lookup` links to the table that produced it (Settings → HSBC, admin).
const SOURCE_FIELD = { recipient_name: "recipient_name", bank_account: "bank_account", bank_name: "bank_name", bank_bic: "bank_bic" };

export const SourceCaption = ({ source, account, edited, onOpenSettings }) => {
  if (edited) return <span className="payment-journal-source tw-text-geist-accent">Edited — Save to keep</span>;
  if (!source) return null;
  const text = {
    ax: <>From vendor <b>{account}</b> in AX</>,
    request: "From the payment request",
    entered: "Entered by Accounting",
    bic_lookup: <>From the bank name lookup ·{" "}
      {onOpenSettings
        ? <button type="button" className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-geist-accent hover:tw-underline"
            onClick={() => onOpenSettings("hsbc_keys")}>Settings → HSBC</button>
        : "Settings → HSBC (admin)"}</>,
  }[source];
  return text ? <span className="payment-journal-source">{text}</span> : null;
};

SourceCaption.propTypes = { source: PropTypes.string, account: PropTypes.string, edited: PropTypes.bool, onOpenSettings: PropTypes.func };

const money = (v) => (Number(v) || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

/**
 * Who receives the money — on the Payment journal (APP), the journal that pays.
 * Filled for each payment; required when the offset account is HSBC* (the bank
 * file is built from it), optional for any other bank.
 */
const PaymentDetailsFields = ({ payment, onChange, requestId, disabled, lockRecipient, hsbc, paymentAmount, xmlV3, erpVendor,
  sources = {}, sourcePayment = {}, onOpenSettings }) => {
  const edited = (key) => String(payment[key] || "") !== String(sourcePayment[key] || "");
  const caption = (key, sourceKey = key) => (!edited(key) && !sources[sourceKey] ? null : (
    <SourceCaption source={sources[sourceKey]} account={erpVendor?.account} edited={edited(key)} onOpenSettings={onOpenSettings} />
  ));
  const v3Type = xmlV3?.enabled ? effectivePaymentType(payment, xmlV3, paymentAmount.currency) : null;
  // Domestic XMLv3 (LTR / RTP): the bank code — required there — identifies the bank,
  // so no BIC is needed (as the live IFILE). Only FTR needs the BIC.
  const bicOptional = v3Type && v3Type !== "FTR";
  // Bank account and bank name read from the vendor in AX: change them on the vendor.
  const fromAx = !!erpVendor?.bank_account;
  const axLocked = (key) => fromAx && (key === "bank_account" || (key === "bank_name" && !!erpVendor.bank_name));
  const requiredKeys = ["recipient_name", "bank_account", "bank_name", ...(bicOptional ? [] : ["bank_bic"])];
  const set = (key, value) => onChange((p) => ({
    ...p, [key]: value,
    // A new recipient invalidates the account picked for the old one; a new bank, its BIC.
    // Bank name and bank account stay independent.
    ...(key === "recipient_name" ? { bank_account: lockRecipient ? p.bank_account : "" } : {}),
    ...(key === "bank_name" ? { bank_bic: "" } : {}),
  }));
  const pick = (entry) => onChange((p) => ({
    ...p, recipient_name: entry.context?.payee || p.recipient_name,
    bank_name: entry.context?.bank_name || p.bank_name, bank_bic: entry.context?.bank_bic || "",
  }));
  const missing = hsbc
    ? [...FIELDS.filter(([key]) => requiredKeys.includes(key) && !String(payment[key] || "").trim()).map(([, label]) => label),
       ...(v3Type ? missingXmlV3(payment, v3Type) : [])]
    : [];

  return (
    <section aria-label="Payment details" className="payment-journal-setup payment-journal-payment">
      <p className="payment-journal-section-title">
        Payment details{hsbc ? " · required for HSBC online payment" : " · optional for other banks"}
      </p>
      <div className="payment-journal-payment-fields">
        {FIELDS.map(([key, label, kind]) => (
          <label key={key} className="tw-block" data-missing={missing.includes(label) || undefined}>
            <span className="payment-journal-setup-label">{label}{hsbc && requiredKeys.includes(key) && <span className="tw-text-[#b42318]"> *</span>}</span>
            <EntryPicker kind={kind} value={payment[key] || ""} paymentRequestId={requestId}
              disabled={disabled || axLocked(key) || (lockRecipient && ["recipient_name", "bank_account"].includes(key))}
              context={kind === "bank_account" ? { payee: payment.recipient_name, bank_name: payment.bank_name, bank_bic: payment.bank_bic } : {}}
              aria-label={`Accounting ${label}`} onChange={(value) => set(key, value)}
              onSelect={kind === "bank_account" ? pick : undefined} />
            {SOURCE_FIELD[key] && caption(key)}
          </label>
        ))}
        {/* What the bank pays: the Payment journal's vendor line, not typed again here. */}
        <label className="tw-block">
          <span className="payment-journal-setup-label">Amount to pay</span>
          {/* Read-only: shown in an input so it reads like the fields around it. */}
          <UnderlineInput aria-label="Amount to pay" readOnly disabled
            value={paymentAmount.amount == null ? "" : `${money(paymentAmount.amount)} ${paymentAmount.currency}`}
            placeholder="—" className="tw-tabular-nums tw-font-semibold" />
          <span className="tw-block tw-mt-1 tw-text-[12px] tw-text-geist-fg-3">From the Payment journal line</span>
        </label>
      </div>
      {erpVendor?.account && (
        <p role="note" aria-label="Bank from AX" className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-geist-fg-3">
          {fromAx
            ? <>Bank account and bank name from vendor <b>{erpVendor.account}</b> in AX — change them on the vendor in AX.</>
            : <>Vendor <b>{erpVendor.account}</b> has no bank account in AX — enter it here.</>}
        </p>
      )}
      {/* HSBC trade loan — chỉ ở Payment - APP trả từ tài khoản HSBC* (component này chỉ
          render khi đó; `hsbc` canh thêm). Mọi loại lệnh, kể cả trả NCC trong nước bằng VND. */}
      {hsbc && (
        <label className="tw-flex tw-items-center tw-gap-2 tw-mt-1 tw-text-[13px] tw-text-geist-fg-2 tw-cursor-pointer">
          <input type="checkbox" aria-label="Trade loan" disabled={disabled}
            checked={payment.trade_loan === "true" || payment.trade_loan === true}
            onChange={(e) => onChange((p) => ({ ...p, trade_loan: e.target.checked ? "true" : "false" }))} />
          HSBC trade loan
          <i className="bi bi-info-circle tw-text-[12px] tw-text-geist-fg-3" role="img" tabIndex={0}
            title="Loan disbursement from an HSBC trade loan: LTR (domestic) or FTR (overseas), not RTP. The recipient bank is told to hold for trade financing (/INT/HOLD/, /INS/TRADE LOAN), and after BOD the Payment Accountant sends it."
            aria-label="About HSBC trade loan" />
        </label>
      )}
      {xmlV3?.enabled && <HsbcXmlV3Fields payment={payment} onChange={onChange} v3={xmlV3} currency={paymentAmount.currency} disabled={disabled} />}
      {xmlV3?.enabled && (sources.bank_clearing_code || sources.address) && (
        <p role="note" aria-label="Sources" className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-geist-fg-3">
          Bank code: {caption("bank_clearing_code") || "not in AX yet — enter it"}
          {" · "}Address: {caption("recipient_street", "address") || "not in AX — enter it if the bank needs it"}
        </p>
      )}
      {missing.length > 0 && (
        <p role="note" className="tw-m-0 tw-mt-2 tw-text-[12px] tw-text-[#b42318]">
          Missing for the HSBC file: {missing.join(", ")}.
        </p>
      )}
    </section>
  );
};

PaymentDetailsFields.propTypes = {
  payment: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  disabled: PropTypes.bool,
  // Single-vendor request: recipient and account come from the request itself.
  lockRecipient: PropTypes.bool,
  hsbc: PropTypes.bool,
  // { amount, currency } — the APP vendor line's Debit / Credit, in the header currency.
  paymentAmount: PropTypes.shape({ amount: PropTypes.number, currency: PropTypes.string }).isRequired,
  // HSBC XMLv3 state from the server (enabled once Settings → HSBC has the customer ID).
  xmlV3: PropTypes.object,
  // { account, name, bank_name, bank_account } read from the vendor in AX.
  erpVendor: PropTypes.object,
  // Where each value comes from (server) and the values it described — a value typed
  // since then shows "Edited".
  sources: PropTypes.object,
  sourcePayment: PropTypes.object,
  // Opens Payment Request → Settings at a tab (admins only; absent = plain text).
  onOpenSettings: PropTypes.func,
};

export default PaymentDetailsFields;
